import React, { useState } from "react";
import axios from "axios";
import { useAuth, formatApiError } from "../AuthContext";
import { Lock, LogIn, LogOut, FileText, Users, CalendarDays, ScrollText, ExternalLink } from "lucide-react";
import { useLang } from "../LanguageContext";
import AdminNewsManager from "../components/AdminNewsManager";
import AdminContactMessages from "../components/AdminContactMessages";
import AdminAccessRequests from "../components/AdminAccessRequests";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_ICONS = [FileText, Users, Lock];
const TIMETABLE_URL = "https://docs.google.com/spreadsheets/d/12v2Brc-R4uHxBKQbv11vk1J4hYLhjwI7E8VJsTL3POU/edit?usp=sharing";
const RULES_URL = "https://docs.google.com/document/d/1qju3uoGh9OeSedkVb6nM3w3nKeTZPrIasA2kv5Z6hEs/view";

export default function EspaceMembres() {
  const { t } = useLang();
  const { user, ready, login, logout } = useAuth();
  const [tab, setTab] = useState("login"); // login | request | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [req, setReq] = useState({ full_name: "", email: "", role: "parent", family_name: "", child_name: "", parent_email: "", message: "" });

  const onLogin = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const onRequest = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      // Strip empty optional fields — Pydantic EmailStr rejects "" for parent_email
      const payload = Object.fromEntries(
        Object.entries(req).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
      );
      await axios.post(`${API}/access/request`, payload);
      setNotice(t("memberx.request_sent")); setTab("done");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const onForgot = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setNotice(t("memberx.forgot_sent")); setTab("done");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  if (!ready) {
    return <div className="container-page py-24 text-ink-muted" data-testid="members-loading">{t("common.loading")}</div>;
  }

  // ---- Logged-in dashboard ----
  if (user) {
    const cards = t("members.cards") || [];
    return (
      <div className="container-page py-16 sm:py-24" data-testid="members-dashboard">
        <p className="eyebrow mb-4">{t("members.eyebrow")}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink mb-2">
          {t("members.welcome")} {user.name}.
        </h1>
        <p className="text-ink-muted mb-10">{t("members.role_label")} <strong>{t(`memberx.role_${user.role}`) || user.role}</strong>.</p>

        <h2 className="font-heading text-2xl font-semibold text-ink mb-4">{t("memberx.resources_title")}</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <a href={TIMETABLE_URL} target="_blank" rel="noopener noreferrer" className="card-soft p-6 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid="member-timetable">
            <div className="w-11 h-11 rounded-2xl bg-forest-light grid place-items-center text-forest shrink-0"><CalendarDays size={20} /></div>
            <span className="font-medium text-ink flex-1">{t("memberx.timetable")}</span>
            <ExternalLink size={16} className="text-ink-muted" />
          </a>
          <a href={RULES_URL} target="_blank" rel="noopener noreferrer" className="card-soft p-6 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid="member-rules">
            <div className="w-11 h-11 rounded-2xl bg-forest-light grid place-items-center text-forest shrink-0"><ScrollText size={20} /></div>
            <span className="font-medium text-ink flex-1">{t("memberx.rules")}</span>
            <ExternalLink size={16} className="text-ink-muted" />
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => {
            const Icon = CARD_ICONS[i] || FileText;
            return (
              <div key={i} className="card-soft p-7">
                <Icon size={20} className="text-forest mb-3" />
                <h3 className="font-heading text-lg font-semibold text-ink mb-1">{c.title}</h3>
                <p className="text-sm text-ink-muted">{c.text}</p>
              </div>
            );
          })}
        </div>

        <button onClick={logout} className="mt-10 inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-cream-300 text-ink-muted hover:text-forest hover:border-forest transition" data-testid="members-logout">
          <LogOut size={16} /> {t("members.logout")}
        </button>

        {user.role === "admin" && (
          <>
            <AdminAccessRequests />
            <AdminNewsManager />
            <AdminContactMessages />
          </>
        )}
      </div>
    );
  }

  // ---- Public: login / request / forgot ----
  const field = (label, value, onChange, type = "text", required = true, testid, placeholder = "") => (
    <div>
      <label className="block text-sm font-medium text-ink mb-1.5">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="field" placeholder={placeholder} data-testid={testid} />
    </div>
  );

  return (
    <div className="container-page py-16 sm:py-24" data-testid="members-login">
      <div className="max-w-md mx-auto">
        <img src="/logo.png" alt="École Pleine Nature" className="w-16 h-16 object-contain mx-auto mb-6" />
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink text-center mb-2">{t("members.title")}</h1>
        <p className="text-center text-ink-muted mb-8">{t("members.subtitle")}</p>

        {tab === "done" ? (
          <div className="card-soft p-8 text-center" data-testid="members-notice">
            <p className="text-ink mb-6">{notice}</p>
            <button onClick={() => { setTab("login"); setError(""); }} className="btn-primary" data-testid="members-back-login">{t("memberx.back_login")}</button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-6 bg-cream-100 rounded-full p-1" data-testid="members-tabs">
              <button onClick={() => { setTab("login"); setError(""); }} className={`flex-1 rounded-full py-2 text-sm font-medium transition ${tab === "login" ? "bg-forest text-cream-50" : "text-ink-muted"}`} data-testid="tab-login">{t("memberx.tab_login")}</button>
              <button onClick={() => { setTab("request"); setError(""); }} className={`flex-1 rounded-full py-2 text-sm font-medium transition ${tab === "request" ? "bg-forest text-cream-50" : "text-ink-muted"}`} data-testid="tab-request">{t("memberx.tab_request")}</button>
            </div>

            {error && <div className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 text-sm mb-4" data-testid="members-error">{error}</div>}

            {tab === "login" && (
              <form onSubmit={onLogin} className="card-soft p-7 space-y-4">
                {field(t("members.email"), email, setEmail, "email", true, "members-input-email", t("contact.form_email_placeholder"))}
                {field(t("members.password"), password, setPassword, "password", true, "members-input-password", "••••••••")}
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="members-submit">
                  {loading ? t("members.submitting") : t("members.submit")} <LogIn size={16} />
                </button>
                <button type="button" onClick={() => { setTab("forgot"); setError(""); }} className="w-full text-center text-sm text-forest hover:underline" data-testid="forgot-link">{t("memberx.forgot")}</button>
              </form>
            )}

            {tab === "request" && (
              <form onSubmit={onRequest} className="card-soft p-7 space-y-4">
                <p className="text-sm text-ink-muted">{t("memberx.request_intro")}</p>
                <div className="rounded-2xl bg-cream-100 border border-forest/15 px-4 py-3 text-sm text-ink" data-testid="req-email-note">
                  {t("memberx.request_email_note")}
                </div>
                {field(t("memberx.f_name"), req.full_name, (v) => setReq({ ...req, full_name: v }), "text", true, "req-name")}
                {field(t("members.email"), req.email, (v) => setReq({ ...req, email: v }), "email", true, "req-email")}
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">{t("memberx.f_role")}</label>
                  <select value={req.role} onChange={(e) => setReq({ ...req, role: e.target.value })} className="field" data-testid="req-role">
                    <option value="parent">{t("memberx.role_parent")}</option>
                    <option value="child">{t("memberx.role_child")}</option>
                  </select>
                </div>
                {field(t("memberx.f_family"), req.family_name, (v) => setReq({ ...req, family_name: v }), "text", false, "req-family")}
                {req.role === "child" && (
                  <>
                    {field(t("memberx.f_child"), req.child_name, (v) => setReq({ ...req, child_name: v }), "text", false, "req-child")}
                    {field(t("memberx.f_parent_email"), req.parent_email, (v) => setReq({ ...req, parent_email: v }), "email", false, "req-parent-email")}
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">{t("memberx.f_message")}</label>
                  <textarea rows={3} value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} className="field" data-testid="req-message" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="req-submit">{t("memberx.request_submit")}</button>
              </form>
            )}

            {tab === "forgot" && (
              <form onSubmit={onForgot} className="card-soft p-7 space-y-4">
                {field(t("members.email"), email, setEmail, "email", true, "forgot-input-email", t("contact.form_email_placeholder"))}
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="forgot-submit">{t("memberx.forgot_submit")}</button>
                <button type="button" onClick={() => { setTab("login"); setError(""); }} className="w-full text-center text-sm text-forest hover:underline" data-testid="forgot-back">{t("memberx.back_login")}</button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
